/**
 * Colour utilities.
 */

import { hexLuminance } from "./accessibleAccent";

/**
 * The luminance above which a background is treated as light enough to need
 * dark text. Chosen so mid-dark brand primaries such as the WBAI orange keep
 * white text, while light primaries switch to dark text for contrast.
 *
 * Deliberately not a WCAG ratio. This picks the better of two fixed foregrounds
 * rather than guaranteeing a threshold, which is what `ensureAccessibleAccent`
 * is for. Only the luminance formula is shared between the two.
 */
const READABLE_TEXT_LUMINANCE_THRESHOLD = 0.45;

/**
 * Returns a readable foreground colour (near-black or white) for text/icons
 * placed on top of the given background colour, chosen by WCAG relative
 * luminance. Falls back to white for unparseable input.
 *
 * Used for the send button, whose background is the brand primary. The brand
 * object exposes no foreground token, so we derive a contrast-safe colour here
 * rather than hardcoding "white" (which would fail on a light brand primary).
 *
 * @param backgroundHex - Background colour as #RGB or #RRGGBB.
 * @returns "#1f2937" (dark) on light backgrounds, "#ffffff" on dark ones.
 */
export function getReadableTextColor(backgroundHex: string): string {
  const luminance = hexLuminance(backgroundHex);

  if (luminance === null) return "#ffffff";

  return luminance > READABLE_TEXT_LUMINANCE_THRESHOLD ? "#1f2937" : "#ffffff";
}
